import AppForm from '@/components/AppForm';
import { SuccessResponseModal, UnsavedChangesModal } from '@/components/AppModal/modals';
import FormUpload from '@/components/common/FormUpload';
import { useUnsaved } from '@/contexts/UnsavedContext';
import useFormSubmit from '@/hooks/useFormSubmit';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';
import usePrizeManagement from '@/redux/hooks/usePrizeManagement';
import { updatePieceList } from '@/services/piece.service';
import { formatNumber } from '@/utils/formatters';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Col, Form, Input, InputNumber, Modal, Row, Space, Switch } from 'antd';
import React, { useMemo } from 'react';

function PieceUpdate({ openPieceModal, setOpenPieceModal, isNewPiece, setPieceList }) {
  const {
    prizeManagement: { piece },
    clearPieceValue,
  } = usePrizeManagement();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { hasUnsaved, setHasUnsaved } = useUnsaved();

  const initialValues = useMemo(() => {
    let imageList = [];

    if (piece) {
      if (isNewPiece && piece.image_file) {
        imageList = [
          {
            uid: '-1',
            name: `Piece ${piece.name}`,
            status: 'done',
            originFileObj: piece.image_file,
            url: URL.createObjectURL(piece.image_file),
          },
        ];
      } else if (!isNewPiece && piece.image_path) {
        imageList = [
          {
            uid: '-1',
            name: `Piece ${piece.name}`,
            status: 'done',
            url: `${process.env.REACT_APP_API_BASEURL}${piece.image_path}`,
          },
        ];
      }
    }

    return {
      ...piece,
      is_active: piece?.is_active ? true : false,
      is_limited: piece?.is_limited ? true : false,
      image: imageList,
    };
  }, [piece, isNewPiece]);

  const pieceMutation = useMutation({
    mutationFn: updatePieceList,
  });

  const onSubmit = {
    run: useFormSubmit(
      pieceMutation,
      async (values) => {
        const imageFile = values.image?.[0]?.originFileObj || values.image?.[0];
        const { image, ...restValues } = values;
        const payload = {
          ...restValues,
          name: values.name.trim(),
          piece_id: initialValues?.id,
          prize_id: initialValues?.prize_id,
          ordinal_number: values.ordinal_number ? parseInt(values.ordinal_number, 10) : null,
          is_active: values.is_active ? 1 : 0,
          is_limited: values.is_limited ? 1 : 0,
        };

        const formData = new FormData();

        Object.keys(payload).forEach((key) => {
          if (typeof payload[key] === 'object') {
            formData.append(key, JSON.stringify(payload[key]));
          } else {
            formData.append(key, payload[key]);
          }
        });

        if (imageFile) {
          formData.append('image', imageFile);
        }

        return formData;
      },
      {
        onSuccess: () => {
          SuccessResponseModal({
            onOk: () => {
              setHasUnsaved(true);
              setOpenPieceModal(false);
              queryClient.invalidateQueries({
                queryKey: ['pieceList', piece?.prize_id],
              });
            },
          });
        },
      }
    ),

    runLocal: (values) => {
      const imageFile = values.image?.[0]?.originFileObj || values.image?.[0];
      const { image, ...restValues } = values;
      setPieceList((prev) => {
        return prev.map((item) =>
          item.id === piece.id ? { ...item, ...restValues, image_file: imageFile } : item
        );
      });

      setOpenPieceModal(false);
    },
  };

  const handleCancel = () => {
    if (form.isFieldsTouched() && hasUnsaved) {
      UnsavedChangesModal(async () => {
        await setOpenPieceModal(false);
        form.resetFields();
        clearPieceValue();
      });
    } else {
      setOpenPieceModal(false);
      clearPieceValue();
      form.resetFields();
    }
  };

  // useEffect(() => {
  //   // Reset form touched state when modal opens
  //   if (openPieceModal) {
  //     form.resetFields();
  //   }
  // }, [form, openPieceModal]);

  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  return (
    <Modal
      open={openPieceModal}
      title={<p>Update Piece</p>}
      width={1200}
      onCancel={() => {
        setOpenPieceModal(false);
        form.isFieldsTouched(false);
        form.resetFields();
      }}
      maskClosable={false}
      centered
      afterClose={() => {
        clearPieceValue();
        // form.resetFields();
      }}
      footer={
        <Space className="mt-6">
          <Button
            htmlType="button"
            onClick={handleCancel}
            type="text"
            className="font-medium"
            disabled={pieceMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            className="font-medium size-16 h-8"
            disabled={pieceMutation.isPending}
            loading={pieceMutation.isPending}
            onClick={() => form.submit()}
          >
            Save
          </Button>
        </Space>
      }
    >
      <AppForm
        form={form}
        initialValues={initialValues}
        onSubmit={(values) => {
          if (isNewPiece) {
            onSubmit.runLocal(values);
          } else {
            onSubmit.run(values);
          }
        }}
        className=""
        hasButtons={false}
        isCard={false}
      >
        <Row gutter={24}>
          <Col span={isMobile ? 24 : 20}>
            <Form.Item
              label="Piece Name"
              name="name"
              rules={[{ message: 'Please enter piece name', required: true }]}
            >
              <Input placeholder="Enter piece name" />
            </Form.Item>
            <Form.Item
              label="Piece Position"
              name="ordinal_number"
              rules={[{ message: 'Please enter piece position', required: true }]}
            >
              <Input type="number" placeholder="Enter piece position" min={1} />
            </Form.Item>
            <Form.Item
              label="Drop Rate"
              name="percent"
              rules={[{ message: 'Please enter drop rate', required: true }]}
            >
              <Input
                type="number"
                addonBefore="%"
                className="w-full"
                placeholder="Enter drop rate"
                min={0}
                max={100}
              />
            </Form.Item>
            <Form.Item
              label="Quantity"
              name="quantity_piece"
              rules={[{ message: 'Please enter quantity', required: true }]}
            >
              <InputNumber
                className="w-full"
                placeholder="Enter prize number"
                min={0}
                formatter={(value) => formatNumber(value)}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
            <Form.Item
              label="Image"
              name="image"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e?.fileList;
              }}
              rules={[{ message: 'Please upload image', required: true }]}
            >
              <FormUpload beforeUpload={() => false} />
            </Form.Item>
          </Col>
          <Col span={isMobile ? 24 : 4}>
            <Form.Item label="Is Limited" name="is_limited" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Is Active" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </AppForm>
    </Modal>
  );
}

export default PieceUpdate;
